import {
  imprint,
  marketLocationAnnualPlanning,
  marketLocationFeedIn,
  marketLocationForecast,
  marketLocationMasterData,
  marketLocationMasterDataDevices,
  marketLocationMasterDataMachines,
  marketLocationOutages,
  marketLocationRevenueQuarterlyReport,
  marketLocationRevenueReport,
  optimizationDownload,
  optimizationGroups,
  privacy,
  root,
  salesPartnerSettings,
  termsOfService,
  userManagement,
} from '@e2m-customer-portal/core';
import { useFeatureFlags, useMarketLocationId, useSnackbar } from '@e2m-customer-portal/hooks';
import { ResponsiveDrawer, ResponsiveDrawerProps, SearchBar } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import clsx from 'clsx';
import { LinkedSidebarMarketLocationFieldsFragment, MachineTechnology } from 'generated/graphql';
import * as Highcharts from 'highcharts';
import { matchSorter } from 'match-sorter';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { useEffectOnce, useToggle } from 'react-use';
import { mostCompactName } from 'utils/most-compact-name';
import { MarketLocations } from './market-locations/MarketLocations';
import { useSidebarMarketLocationsQuery } from './market-locations/query';

// Backend allows a max page size of 2000
const FETCH_PAGE_SIZE = 1000;
export const SIDEBAR_STORAGE_KEY = 'sidebar-expanded';
export const OUTDATED_BOOKMARKS_KEY = 'outdated-bookmarks';

function markOutdatedBookmarks() {
  localStorage.setItem(OUTDATED_BOOKMARKS_KEY, 'true');
}

interface SidebarProps extends ResponsiveDrawerProps {
  onExpansionChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export function Sidebar(props: Readonly<SidebarProps>): ReactElement<ResponsiveDrawerProps> {
  const [t] = useTranslation();
  const [filter, setFilter] = useState('');

  const [expanded, toggleExpanded] = useToggle(
    Boolean(JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) ?? 'true')),
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { showError } = useSnackbar();

  const marketLocationId = useMarketLocationId();

  const { loading, error, data, fetchMore } = useSidebarMarketLocationsQuery({
    variables: { first: FETCH_PAGE_SIZE },
  });

  if (error) {
    throw error;
  }

  const marketLocations: ReadonlyArray<LinkedSidebarMarketLocationFieldsFragment> =
    data?.marketLocations.edges
      .map((edge) => edge.node)
      .slice()
      .sort((a, b) =>
        (a.externalName || a.name).toLocaleLowerCase() < (b.externalName || b.name).toLocaleLowerCase() ? -1 : 1,
      ) ?? [];

  if (data) {
    // Temporary redirects for old routes
    if (/impressum\b/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(imprint.getLink(), { replace: true });
    } else if (/datenschutz\b/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(privacy.getLink(), { replace: true });
    } else if (/nutzungsbedingungen\b/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(termsOfService.getLink(), { replace: true });
    } else if (/optimierung(\b|\/.*)/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(optimizationDownload.getLink(), { replace: true });
    } else if (/vertriebspartner\b/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(salesPartnerSettings.getLink(), { replace: true });
    } else if (/benutzerverwaltung\b/.test(pathname)) {
      markOutdatedBookmarks();
      navigate(userManagement.getLink(), { replace: true });
    } else if (/optimierungsgruppen\b/.test(pathname) && marketLocationId) {
      // Optimization group pages can’t be linked to at the moment anyway, so we don’t show
      // the banner in this case. (We still redirect to the correct market location, though.)
      navigate(optimizationGroups.getLink(marketLocationId), { replace: true });
    } else if (/zeitreihen\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationFeedIn.getLink(marketLocationId), { replace: true });
    } else if (/wochenplanung\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationForecast.getLink(marketLocationId), { replace: true });
    } else if (/ausf(%C3%A4|ä)lle\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationOutages.getLink(marketLocationId), { replace: true });
    } else if (/jahresplanung\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationAnnualPlanning.getLink(marketLocationId), { replace: true });
    } else if (/maschinen\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationMasterDataMachines.getLink(marketLocationId), { replace: true });
    } else if (/technische-einheiten\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationMasterDataDevices.getLink(marketLocationId), { replace: true });
    } else if (/stammdaten\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationMasterData.getLink(marketLocationId), { replace: true });
    } else if (/quartal\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationRevenueQuarterlyReport.getLink(marketLocationId), { replace: true });
    } else if (/erl(%C3%B6|ö)s(%C3%BC|ü)bersicht\b/.test(pathname) && marketLocationId) {
      markOutdatedBookmarks();
      navigate(marketLocationRevenueReport.getLink(marketLocationId), { replace: true });
    }

    const mainMaLoForHiddenMaLo = marketLocations.find((maLo) => maLo.linkedMarketLocation?.id === marketLocationId);
    if (marketLocationId && mainMaLoForHiddenMaLo) {
      navigate(marketLocationFeedIn.getLink(mainMaLoForHiddenMaLo.id), { replace: true });
    }

    const isRoot = root.test(pathname, undefined, { end: true });
    if (isRoot && data.marketLocations.edges.length > 0) {
      navigate(marketLocationFeedIn.getLink(data.marketLocations.edges[0].node.id), { replace: true });
    }
  }

  // Don’t show at all for now, we use tabs in the meantime instead
  const hasOptimizationGroup = false;

  useEffect(() => {
    if (data?.marketLocations.pageInfo.hasNextPage) {
      fetchMore({
        variables: {
          first: FETCH_PAGE_SIZE,
          after: data.marketLocations.pageInfo.endCursor,
        },
      }).catch((err) => {
        console.error(err);
        showError(t('SHARED_ERROR_UNKNOWN'));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fetchMore]);

  const rerenderHighcharts = () => {
    setTimeout(() => {
      Highcharts.charts.forEach((chart) => {
        if (!chart?.container) return;
        chart.container.style.maxWidth = '0';
      });

      Highcharts.charts.forEach((chart) => {
        if (!chart?.container) return;
        chart.reflow();
      });

      Highcharts.charts.forEach((chart) => {
        if (!chart?.container) return;
        chart.container.style.maxWidth = '';
      });
    }, 350);
  };

  useEffect(() => {
    rerenderHighcharts();
    window.addEventListener('resize', rerenderHighcharts);
    return () => window.removeEventListener('resize', rerenderHighcharts);
  }, [expanded]);

  const toggleSidebarExpansion = (expand: boolean) => {
    toggleExpanded(expand);
    props.onExpansionChange(expand);
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(expand));
    rerenderHighcharts();
  };

  useEffectOnce(() => toggleSidebarExpansion(expanded));

  const filteredLocations = marketLocations
    ? matchSorter(marketLocations, filter.replaceAll(' ', ''), {
        keys: [
          (item: LinkedSidebarMarketLocationFieldsFragment) => item.contractPartner.replaceAll(' ', ''),
          (item: LinkedSidebarMarketLocationFieldsFragment) => item.name.replaceAll(' ', ''),
          (item: LinkedSidebarMarketLocationFieldsFragment) => item.externalName.replaceAll(' ', ''),
          (item: LinkedSidebarMarketLocationFieldsFragment) => item.technologies.join().replaceAll(' ', ''),
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.technologies
              .map((technology) => t(`SHARED_TECHNOLOGY_${technology}`))
              .join()
              .replaceAll(' ', ''),
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation ? item.linkedMarketLocation?.contractPartner.replaceAll(' ', '') : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation ? item.linkedMarketLocation?.name.replaceAll(' ', '') : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation ? item.linkedMarketLocation?.externalName.replaceAll(' ', '') : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation ? item.linkedMarketLocation?.technologies.join().replaceAll(' ', '') : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation
              ? item.linkedMarketLocation.technologies
                  .map((technology) => t(`SHARED_TECHNOLOGY_${technology}`))
                  .join()
                  .replaceAll(' ', '')
              : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            item.linkedMarketLocation
              ? mostCompactName(item.name, item.linkedMarketLocation?.name).replaceAll(' ', '')
              : '',
          (item: LinkedSidebarMarketLocationFieldsFragment) =>
            (item.technologies
              .concat(item.linkedMarketLocation?.technologies ?? [])
              .includes(MachineTechnology.COMPOSITE)
              ? t('EASTER_EGG_COMPOSITE_DEVICE')
              : ''
            ).replaceAll(' ', ''),
        ],
        threshold: matchSorter.rankings.ACRONYM,
      })
    : [];

  const { areOptimizationGroupsEnabled } = useFeatureFlags();
  const optimizationGroupsPageSelected = optimizationGroups.test(pathname);

  return (
    <ResponsiveDrawer
      {...props}
      expanded={expanded}
      onToggleExpandedClick={() => toggleSidebarExpansion(!expanded)}
      searchBar={<SearchBar inputValue={filter} setInputValue={setFilter} />}
    >
      <>
        {areOptimizationGroupsEnabled && hasOptimizationGroup && (
          <div
            data-testid="sidebar-option-optimization-groups"
            role="option"
            aria-selected={optimizationGroupsPageSelected}
          >
            <Button
              className={clsx(
                'flex flex-col items-start box-border w-[17.5rem] h-[3.75rem] mt-[0.125rem] ml-5 pl-4 rounded-none rounded-l-lg hover:bg-green-50 hover:text-green-200 focus-visible:bg-green-50 focus-visible:text-black',
                optimizationGroupsPageSelected ? 'bg-green-50 text-green-200' : 'bg-grey-50',
              )}
              color={optimizationGroupsPageSelected ? 'primary' : 'inherit'}
              component={Link}
              to={optimizationGroups.getLink(marketLocationId ?? '11')}
            >
              {t('HEADER_MENU_OPTIMIZATION_GROUPS')}
            </Button>
          </div>
        )}
        {filteredLocations.length || loading ? (
          <MarketLocations marketLocations={filteredLocations} loading={loading} />
        ) : (
          <div
            className="ml-5 mt-0.5 rounded-l-lg bg-grey-50 py-5 pl-4 text-sm font-semibold"
            data-testid="sidebar-no-results"
          >
            {marketLocations.length
              ? t('SIDEBAR_MARKET_LOCATION_NO_RESULTS', { filter })
              : t('SIDEBAR_MARKET_LOCATION_MISCONFIGURED')}
          </div>
        )}
      </>
    </ResponsiveDrawer>
  );
}
